/**
 * Workflow Diagram
 *
 *        ┌─────────────────────────────┐
 *        │   Redux Store (district)    │
 *        └───────────────┬─────────────┘
 *                        │
 *                        ▼
 *        ┌─────────────────────────────┐
 *        │ Extract invite_code          │
 *        │   district?.invite_code      │
 *        └───────────────┬─────────────┘
 *                        │
 *          invite_code ?  │   :   null
 *                        │
 *            ┌───────────┼───────────┐
 *            │           │           │
 *            ▼           ▼           ▼
 *  Show Code in UI   Show Skeleton   Disable Copy Button
 *            │                       (until code is loaded)
 *            ▼
 *   ┌─────────────────────────────┐
 *   │ User clicks "Copy" button   │
 *   └───────────────┬─────────────┘
 *                   │
 *                   ▼
 *   ┌─────────────────────────────┐
 *   │ clickCopyEvent(text)        │
 *   │ → navigator.clipboard.write │
 *   └───────────────┬─────────────┘
 *                   │
 *        ┌──────────┼──────────┐
 *        │ Success   │ Failure │
 *        ▼           ▼
 *   showAlerts(     showAlerts(
 *    "Invite..."     "Something..."
 *   )               )
 */

import React, { useCallback } from "react";
import { Button, Divider, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { useAlerts } from "contexts";
import { CopyIcon } from "components/icon";
import { useSelector } from "react-redux";
import { selectDistrictData } from "store/slices";
import { clickCopyEvent } from "utils";
import { useTranslation } from "react-i18next";

const InviteCode = () => {
    const { t } = useTranslation("layout");
    const { showAlerts } = useAlerts();
    const { invite_code } = useSelector(selectDistrictData); // Get district info from Redux store

    // Handles copy-to-clipboard action
    const clickEvent = useCallback(async(text) => {
        const res = await clickCopyEvent(text);
        if(res) {
            showAlerts(t("inviteCodeCopied"));
        } else {
            showAlerts(t("somethingWentWrong"), null, "error");            
        }
    },[showAlerts, t]);

    return (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid #DBDDE0",
                background: "#F4F4F5",
                // position: "absolute",
                // bottom: 1,
                // margin: 1,
                paddingX: 2,
                paddingY: 2.5,
                width: `calc(100% - 0px)`,
            }}
        >
            <Stack direction={"column"} sx={{
                gap: 2
            }}>
                {/* Section heading */}
                <Stack direction={"column"} sx={{
                    gap: 0.5
                }}>
                    <Typography variant="h5">{t("inviteCode")}</Typography>
                    <Typography variant="h6">
                        {t("copyDistrictsInviteCodeShare")}
                    </Typography>
                </Stack>

                {/* Invite code display + copy button */}
                <Stack
                    direction={"row"}
                    sx={{
                        alignItems: "center",
                        gap: 0,
                        border: "1px solid #DBDDE0",
                        background: "#F9FAFB",
                        borderRadius: 1
                    }}>
                    {/* Show invite code or loading skeleton */}
                    <Typography
                        variant="subtitle1"
                        sx={{ flexGrow: 1, paddingLeft: 1.75, color: "#667085" }}
                    >
                        {invite_code || <Skeleton />}
                    </Typography>

                    <Divider orientation="vertical" flexItem />

                    {/* Copy button - disabled if invite_code not loaded */}
                    <Button
                        variant="text"
                        disabled={!invite_code}
                        sx={{ background: "#FFFFFF", width: 100 }}
                        startIcon={<CopyIcon fontSize="inherit" />}
                        onClick={() => clickEvent(invite_code)}
                        aria-label={t("copyInviteCode")}
                    >
                        {t("copy")}
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    );
};

export default React.memo(InviteCode);
